import React, { useCallback, useEffect, useRef } from 'react';
import { MapPin } from 'lucide-react';
import { formatPrice, formatDistanceAndTime, formatRelativeTime } from '@/utils/formatters';
import type { StationRecommendation } from '@/types';

interface StationListPanelProps {
  recommendations: StationRecommendation[];
  selectedStation: StationRecommendation | null;
  onStationSelect: (recommendation: StationRecommendation) => void;
  isLoading?: boolean;
  className?: string;
  tripType?: 'single' | 'return';
  sortBy?: string;
  p25FuelPrice?: number;
  p75FuelPrice?: number;
}

export const StationListPanel: React.FC<StationListPanelProps> = React.memo(({
  recommendations,
  selectedStation,
  onStationSelect,
  isLoading = false,
  className = '',
  tripType = 'single',
  sortBy = 'total_cost',
  p25FuelPrice = 0,
  p75FuelPrice = 0,
}) => {
  const selectedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    selectedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selectedStation?.station.id]);

  const handleStationSelect = useCallback((recommendation: StationRecommendation) => {
    onStationSelect(recommendation);
  }, [onStationSelect]);

  if (isLoading) {
    return (
      <div className={className} style={{ background: 'var(--paper-2)', height: '100%', overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--paper-line)' }}>
          <div style={{ height: 22, borderRadius: 4, background: 'var(--paper-line)', width: '50%', marginBottom: 8 }} />
          <div style={{ height: 12, borderRadius: 4, background: 'var(--paper-line)', width: '30%' }} />
        </div>
        {[...Array(5)].map((_, i) => (
          <div key={i} style={{ padding: '16px 20px', borderBottom: '1px solid var(--paper-line)' }}>
            <div style={{ height: 14, borderRadius: 4, background: 'var(--paper-line)', width: '35%', marginBottom: 8 }} />
            <div style={{ height: 12, borderRadius: 4, background: 'var(--paper-line)', width: '60%', marginBottom: 6 }} />
            <div style={{ height: 12, borderRadius: 4, background: 'var(--paper-line)', width: '25%' }} />
          </div>
        ))}
      </div>
    );
  }

  if (recommendations.length === 0) {
    return (
      <div className={className} style={{ background: 'var(--paper-2)', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', padding: 24 }}>
          <MapPin style={{ width: 40, height: 40, margin: '0 auto 12px', color: 'var(--paper-dim)', opacity: 0.5 }} />
          <p style={{ fontWeight: 600, color: 'var(--paper-fg)', marginBottom: 6 }}>No fuel stations found</p>
          <p style={{ fontSize: 13, color: 'var(--paper-mute)' }}>Try increasing the search radius or adjusting your filters.</p>
        </div>
      </div>
    );
  }

  const sortLabel = sortBy === 'total_cost' ? 'total cost' :
                    sortBy === 'price' ? 'pump price' :
                    sortBy === 'distance' ? 'distance' : 'total cost';

  return (
    <div className={`flex flex-col overflow-hidden ${className}`} style={{ background: 'var(--paper-2)' }}>
      {/* Header */}
      <div className="flex-shrink-0" style={{
        padding: '14px 20px',
        borderBottom: '1px solid var(--paper-line)',
        position: 'sticky', top: 0,
        background: 'var(--paper-2)', zIndex: 5,
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <h3 className="serif" style={{ fontSize: 22, margin: 0, letterSpacing: '-0.01em', fontWeight: 400, color: 'var(--paper-fg)' }}>
            {recommendations.length} stations <em style={{ color: 'var(--paper-mute)' }}>nearby</em>
          </h3>
          <div style={{ fontSize: 12, color: 'var(--paper-dim)' }}>
            By <span style={{ color: 'var(--paper-mute)', fontWeight: 600 }}>{sortLabel}</span>
          </div>
        </div>
      </div>

      {/* Station List */}
      <div className="station-scroll flex-1 overflow-y-auto" style={{ borderTop: '1px solid var(--paper-line)' }}>
        {recommendations.map((recommendation, index) => {
          const isSelected = selectedStation?.station.id === recommendation.station.id;
          const pp = recommendation.bestPrice?.price_pence;
          const isCheap = p25FuelPrice > 0 && pp != null && pp <= p25FuelPrice;
          const isExpensive = p25FuelPrice > 0 && pp != null && pp >= p75FuelPrice;
          const priceColor = isCheap ? 'var(--green)' : isExpensive ? 'var(--red)' : 'var(--paper-fg)';
          const rankColor = index === 0 ? 'var(--green)' : index <= 2 ? 'var(--amber)' : 'var(--paper-dim)';

          return (
            <div
              key={recommendation.station.id}
              ref={isSelected ? selectedRef : undefined}
              onClick={() => handleStationSelect(recommendation)}
              style={{
                position: 'relative',
                padding: '14px 20px',
                borderBottom: '1px solid var(--paper-line)',
                background: isSelected ? 'rgba(245,165,36,0.06)' : 'var(--paper-2)',
                cursor: 'pointer',
                transition: 'background 150ms ease',
              }}
            >
              {/* Selected left-edge bar */}
              {isSelected && (
                <span style={{
                  position: 'absolute', left: 0, top: 0, bottom: 0,
                  width: 3, background: 'var(--amber)',
                }} />
              )}

              {/* Right-edge price colour bar */}
              <span style={{
                position: 'absolute', right: 0, top: 0, bottom: 0,
                width: 4, borderRadius: '0 4px 4px 0',
                background: isCheap ? 'var(--green)' : isExpensive ? 'var(--red)' : 'var(--amber)',
                opacity: p25FuelPrice === 0 || !pp ? 0 : 1,
              }} />

              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Station header row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{
                      fontSize: 11, fontWeight: 700, minWidth: 22, textAlign: 'center',
                      color: rankColor, fontFamily: 'var(--font-mono)',
                    }}>
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--paper-fg)', letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {recommendation.station.brand}
                    </span>
                    {index === 0 && (
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                        background: 'var(--green-soft)', color: 'var(--green)',
                        border: '1px solid rgba(34,211,154,0.2)', letterSpacing: '0.05em',
                        flexShrink: 0,
                      }}>CHEAPEST</span>
                    )}
                    <span style={{
                      fontSize: 11.5, color: 'var(--paper-dim)',
                      padding: '2px 7px', borderRadius: 999,
                      border: '1px solid var(--paper-line-strong)',
                      flexShrink: 0,
                    }}>
                      {formatDistanceAndTime(
                        tripType === 'return' ? recommendation.distance * 2 : recommendation.distance,
                        tripType === 'return' && recommendation.travelTime ? recommendation.travelTime * 2 : recommendation.travelTime
                      )}
                    </span>
                  </div>

                  {/* Address */}
                  <p style={{ fontSize: 13, color: 'var(--paper-mute)', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {recommendation.station.address}
                  </p>

                  {recommendation.bestPrice?.recorded_at && (
                    <p style={{ fontSize: 11.5, color: 'var(--paper-dim)', marginBottom: 6 }}>
                      Updated {formatRelativeTime(recommendation.bestPrice.recorded_at)}
                    </p>
                  )}

                  {/* Cost breakdown */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--paper-dim)', flexWrap: 'wrap' }}>
                    <span>Travel: <span style={{ color: 'var(--paper-mute)' }}>{formatPrice(recommendation.travelCost)}</span></span>
                    <span style={{ color: 'var(--paper-line-strong)' }}>·</span>
                    <span>Fuel: <span style={{ color: 'var(--paper-mute)' }}>{formatPrice(recommendation.fuelCost)}</span></span>
                    {recommendation.savings !== undefined && (
                      <>
                        <span style={{ color: 'var(--paper-line-strong)' }}>·</span>
                        <span style={{ fontWeight: 600, color: isCheap ? 'var(--green)' : isExpensive ? 'var(--red)' : 'var(--paper-mute)' }}>
                          {recommendation.savings > 0
                            ? `${formatPrice(recommendation.savings)} below avg`
                            : recommendation.savings < 0
                            ? `${formatPrice(Math.abs(recommendation.savings))} above avg`
                            : 'At area average'
                          }
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Price + Total */}
                <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 90 }}>
                  <div style={{ fontSize: 21, fontWeight: 700, color: priceColor, lineHeight: 1, letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>
                    {formatPrice(pp)}
                    <span style={{ fontSize: 11, color: 'var(--paper-dim)', fontWeight: 500 }}>/L</span>
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--paper-dim)', marginTop: 3 }}>
                    {recommendation.bestPrice?.fuel_name}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, marginTop: 4, color: isCheap ? 'var(--green)' : isExpensive ? 'var(--red)' : 'var(--amber)', fontVariantNumeric: 'tabular-nums' }}>
                    {formatPrice(recommendation.totalCost)} total
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.tripType === nextProps.tripType &&
    prevProps.sortBy === nextProps.sortBy &&
    prevProps.className === nextProps.className &&
    prevProps.selectedStation?.station.id === nextProps.selectedStation?.station.id &&
    prevProps.recommendations.length === nextProps.recommendations.length &&
    prevProps.recommendations.every((rec, index) =>
      rec.station.id === nextProps.recommendations[index]?.station.id &&
      rec.bestPrice?.price_pence === nextProps.recommendations[index]?.bestPrice?.price_pence
    ) &&
    prevProps.onStationSelect === nextProps.onStationSelect &&
    prevProps.p25FuelPrice === nextProps.p25FuelPrice &&
    prevProps.p75FuelPrice === nextProps.p75FuelPrice
  );
});

StationListPanel.displayName = 'StationListPanel';

export default StationListPanel;
